/**
 * Minimal EXIF `DateTimeOriginal` reader.
 *
 * Local files only expose `File.lastModified`, which is usually when the file was *copied*.
 * Chaptering on that produces confidently wrong headings — worse than no chapters at all
 * (PLAN.md §7 Q10). This reads the real capture time out of the JPEG header.
 *
 * No dependency, and only the first slice of the file is read.
 */

/** EXIF lives near the start; 128 KB is generous even for files with a big thumbnail. */
const HEADER_BYTES = 128 * 1024;

const TAG_EXIF_IFD_POINTER = 0x8769;
const TAG_DATE_TIME_ORIGINAL = 0x9003;
const TAG_DATE_TIME_DIGITIZED = 0x9004;
const TYPE_ASCII = 2;

/** "2019:07:04 11:23:05" → ISO. EXIF has no timezone, so this is treated as local time. */
function parseExifDateString(value: string): string | null {
  const match = /^(\d{4}):(\d{2}):(\d{2})[ T](\d{2}):(\d{2}):(\d{2})/.exec(value.trim());
  if (!match) return null;
  const [, y, mo, d, h, mi, s] = match;
  const date = new Date(
    Number(y),
    Number(mo) - 1,
    Number(d),
    Number(h),
    Number(mi),
    Number(s)
  );
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

function readAscii(view: DataView, offset: number, length: number): string {
  let out = '';
  for (let i = 0; i < length; i++) {
    const code = view.getUint8(offset + i);
    if (code === 0) break;
    out += String.fromCharCode(code);
  }
  return out;
}

/**
 * Walk one IFD. Returns the date if found here, otherwise the offset of the Exif sub-IFD.
 */
function scanIfd(
  view: DataView,
  tiffStart: number,
  ifdOffset: number,
  little: boolean
): { date?: string; exifIfd?: number } {
  const result: { date?: string; exifIfd?: number } = {};
  const base = tiffStart + ifdOffset;
  if (base + 2 > view.byteLength) return result;

  const entries = view.getUint16(base, little);
  for (let i = 0; i < entries; i++) {
    const entry = base + 2 + i * 12;
    if (entry + 12 > view.byteLength) break;

    const tag = view.getUint16(entry, little);
    const type = view.getUint16(entry + 2, little);
    const count = view.getUint32(entry + 4, little);

    if (tag === TAG_EXIF_IFD_POINTER) {
      result.exifIfd = view.getUint32(entry + 8, little);
      continue;
    }

    if ((tag === TAG_DATE_TIME_ORIGINAL || tag === TAG_DATE_TIME_DIGITIZED) && type === TYPE_ASCII) {
      // Values longer than 4 bytes live elsewhere and the entry holds a pointer.
      const valueOffset =
        count > 4 ? tiffStart + view.getUint32(entry + 8, little) : entry + 8;
      if (valueOffset + count > view.byteLength) continue;
      const parsed = parseExifDateString(readAscii(view, valueOffset, count));
      // DateTimeOriginal wins over Digitized if both are present.
      if (parsed && (tag === TAG_DATE_TIME_ORIGINAL || !result.date)) result.date = parsed;
    }
  }
  return result;
}

function findDateInBuffer(buffer: ArrayBuffer): string | null {
  const view = new DataView(buffer);
  if (view.byteLength < 4) return null;
  if (view.getUint16(0, false) !== 0xffd8) return null; // not a JPEG

  let offset = 2;
  while (offset + 4 <= view.byteLength) {
    if (view.getUint8(offset) !== 0xff) break;

    const marker = view.getUint8(offset + 1);
    if (marker === 0xda) break; // start of scan — headers are done
    const segmentLength = view.getUint16(offset + 2, false);
    if (segmentLength < 2) break;

    if (marker === 0xe1 && offset + 4 + 6 <= view.byteLength) {
      const header = readAscii(view, offset + 4, 4);
      if (header === 'Exif') {
        const tiffStart = offset + 10;
        if (tiffStart + 8 > view.byteLength) return null;

        const endian = view.getUint16(tiffStart, false);
        const little = endian === 0x4949;
        if (!little && endian !== 0x4d4d) return null;

        const firstIfd = view.getUint32(tiffStart + 4, little);
        const ifd0 = scanIfd(view, tiffStart, firstIfd, little);
        if (ifd0.date) return ifd0.date;
        if (ifd0.exifIfd !== undefined) {
          const sub = scanIfd(view, tiffStart, ifd0.exifIfd, little);
          if (sub.date) return sub.date;
        }
        return null;
      }
    }

    offset += 2 + segmentLength;
  }
  return null;
}

/** Returns an ISO timestamp, or null when the file has no usable EXIF date. */
export async function readExifDate(file: File): Promise<string | null> {
  if (!/jpe?g/i.test(file.type)) return null;
  try {
    const buffer = await file.slice(0, HEADER_BYTES).arrayBuffer();
    return findDateInBuffer(buffer);
  } catch {
    return null;
  }
}
